/** The signed-in frame: rail + top bar + page outlet, idle guard, update banner. */
import { useAuth } from '../../lib/auth';
import { RefreshCw } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Spinner, cx } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { applyUpdate, useUpdateReady } from '../../lib/pwa';
import { IdleGuard } from './IdleGuard';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const COLLAPSE_KEY = 'eros.nav.collapsed';

export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const updateReady = useUpdateReady();

  useEffect(() => setMobileOpen(false), [pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });
  };

  return (
    <div className="min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded bg-card px-3 py-2 focus:not-sr-only focus:fixed focus:left-2 focus:top-2">
        Skip to content
      </a>
      <aside className={cx('fixed inset-y-0 left-0 z-40 hidden lg:block', collapsed ? 'w-[72px]' : 'w-64')}>
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div aria-hidden className="absolute inset-0 bg-[rgb(var(--shadow)/0.5)]" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[min(300px,85vw)] shadow-lift">
            <Sidebar collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
      <div className={cx('flex min-h-dvh flex-col', collapsed ? 'lg:pl-[72px]' : 'lg:pl-64')}>
        {updateReady && (
          <button type="button" onClick={() => void applyUpdate()} className="flex w-full items-center justify-center gap-2 bg-info-soft px-4 py-2 text-sm font-semibold text-info">
            <RefreshCw aria-hidden className="h-4 w-4" />A new version of {BRAND.product} is ready. Tap to update.
          </button>
        )}
        <TopBar onMenu={() => setMobileOpen(true)} />
        <MfaOffBanner />
        <main id="main" className="flex-1">
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <IdleGuard />
    </div>
  );
}

/** Owner/admin reminder while Settings › "Require two-step sign-in" is OFF (pilot/demo mode). */
function MfaOffBanner() {
  const { user } = useAuth();
  if (!user || user.roleCode !== 'OWNER_ADMIN' || user.mfaRequired) return null;
  return (
    <div role="status" className="bg-danger px-4 py-1.5 text-center text-sm font-semibold text-white">
      Two-step sign-in is OFF (pilot/demo mode). Turn it back on in Admin › Settings before going live.
    </div>
  );
}
